import React from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import * as Api from 'src/api'
import PortfolioIcon from 'src/assets/icons/customIcons/menu-icons/Portfolio'
import { Plus } from 'src/assets/icons/customIcons/Plus'
import { getStudentProgramByDegree } from 'src/helpers/fns'
import { useAdminAccess, useApi } from 'src/helpers/hooks'
import { useAuthenticatedHeaders } from 'src/hooks/auth/app'
import { useLocale } from 'src/hooks/locale/locale'
import { useTranslatable } from 'src/hooks/locale/utils'
import { useActiveSemester } from 'src/hooks/semesters'
import { Button } from 'src/tailwind/components/Button'
import { Section } from 'src/tailwind/components/Section'
import * as Table from 'src/tailwind/components/Table'
import PortfolioSemesterSelect from 'src/views/includes/PortfolioSemesterSelect'
import SuspenseWrapper from 'src/views/includes/SuspenseWrapper'

export default function AdministratorPortfolioSectionsPage() {
  const t = useTranslatable()

  return (
    <SuspenseWrapper title={t('portfolio:sections')}>
      <PageContent />
    </SuspenseWrapper>
  )
}

function PageContent(): JSX.Element | null {
  const headers = useAuthenticatedHeaders()
  const locale = useLocale()
  const t = useTranslatable()
  const canManagePortfolio = useAdminAccess().canManage('portfolio')
  const activeSemester = useActiveSemester()
  const [searchParams] = useSearchParams({
    semId: activeSemester?.id as string,
  })
  const semId = searchParams.get('semId')

  const { data: sections } = useApi({
    endpoint: Api.getAdministratorPortfolioSections,
    params: React.useMemo(
      () => ({
        headers,
        query: {
          semId: semId!,
        },
      }),
      [headers, semId]
    ),
  })

  const breadcrumbsItems = [
    {
      page: `${t('portfolio:portfolio')} - ${t('portfolio:sections')}`,
      path: `/${locale}/administrator/portfolio/sections`,
    },
  ]

  return (
    <Section
      title={t('portfolio:sections')}
      breadcrubms={breadcrumbsItems}
      icon={<PortfolioIcon />}
      rightElement={
        canManagePortfolio ? (
          <Link to={`/${locale}/administrator/portfolio/sections/add`}>
            <Button variant="blue" className="flex items-center">
              <Plus color="white" /> <span className="ml-2">{t('common:add')}</span>
            </Button>
          </Link>
        ) : undefined
      }
    >
      <PortfolioSemesterSelect />
      <Table.Table className="mt-7 w-full">
        <Table.Thead>
          <Table.Tr>
            <Table.Th>{t('common:unique')}</Table.Th>
            <Table.Th>{t('common:name')}</Table.Th>
            <Table.Th>{t('common:type')}</Table.Th>
            <Table.Th>{t('person:degree')}</Table.Th>
            <Table.Th>{t('portfolio:competency_count')}</Table.Th>
            <Table.Th>{t('common:created_at')}</Table.Th>
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {sections.data.map((section) => (
            <Table.Tr key={section.id}>
              <Table.Td>{section.id}</Table.Td>
              <Table.Td>
                <Link
                  to={`/${locale}/administrator/portfolio/sections/${section.id}/general`}
                  className="mr-2 text-blue-500 hover:underline"
                >
                  {section.name}
                </Link>
              </Table.Td>
              <Table.Td>
                <span className="whitespace-nowrap">
                  {t('common:form')} {section.workType.value}
                </span>
              </Table.Td>
              <Table.Td>{getStudentProgramByDegree(section.degree.value)}</Table.Td>
              <Table.Td>{section.competenciesCount}</Table.Td>
              <Table.Td className="whitespace-nowrap">{section.createdAt}</Table.Td>
            </Table.Tr>
          ))}
        </Table.Tbody>
      </Table.Table>
    </Section>
  )
}
